import { useEffect, useState } from 'react';

/**
 * 389 (#1305, mock-up v2 screen 4): the pop-up as a customer will see it, at phone size, inside the settings card.
 * Swipe with the arrows; dots show the position. Pictures are the platform's addresses or a local preview of a new upload.
 */
export interface PreviewPoster {
  key: string;
  image: string | null;
  button_label: string | null;
  link: string | null;
}

export function HomePopupPreview({ posters }: { posters: PreviewPoster[] }) {
  const [index, setIndex] = useState(0);
  const count = posters.length;
  useEffect(() => {
    if (index > count - 1) setIndex(Math.max(0, count - 1));
  }, [count, index]);

  if (count === 0) {
    return (
      <div
        data-testid="home-popup-preview"
        className="flex h-[560px] w-[300px] items-center justify-center rounded-[22px] bg-[#121212] p-6 text-center text-xs text-[#b3b3b3]"
      >
        Add a poster to preview the pop-up.
      </div>
    );
  }
  const current = posters[Math.min(index, count - 1)] as PreviewPoster;
  return (
    <div
      data-testid="home-popup-preview"
      className="relative flex h-[560px] w-[300px] items-center justify-center overflow-hidden rounded-[22px] bg-[#121212] px-[22px]"
    >
      <div className="relative flex w-full flex-col items-center gap-3">
        <div className="relative aspect-[4/5] w-full">
          {current.image ? (
            <img
              data-testid="home-popup-preview-picture"
              src={current.image}
              alt=""
              className="h-full w-full rounded-[18px] bg-[#252525] object-contain"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center rounded-[18px] bg-[#252525] text-xs text-[#b3b3b3]">
              No picture
            </div>
          )}
          {index > 0 && (
            <button
              type="button"
              aria-label="Previous poster in preview"
              onClick={() => setIndex(index - 1)}
              className="absolute top-[40%] -left-4 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white"
            >
              ‹
            </button>
          )}
          {index < count - 1 && (
            <button
              type="button"
              aria-label="Next poster in preview"
              onClick={() => setIndex(index + 1)}
              className="absolute top-[40%] -right-4 flex h-9 w-9 items-center justify-center rounded-full bg-black/70 text-white"
            >
              ›
            </button>
          )}
          <span
            aria-hidden="true"
            className="absolute -top-2.5 -right-2.5 flex h-9 w-9 items-center justify-center rounded-full bg-white text-lg font-bold text-[#121212]"
          >
            ×
          </span>
        </div>
        <div className="flex h-[42px] w-full items-center">
          {current.button_label && current.link && (
            <span
              data-testid="home-popup-preview-button"
              className="w-full rounded-full bg-brand-yellow px-3 py-2.5 text-center text-[13px] font-bold text-[#121212]"
            >
              {`${current.button_label} ↗`}
            </span>
          )}
        </div>
        {count > 1 && (
          <div className="flex gap-2">
            {posters.map((p, i) => (
              <span
                key={p.key}
                data-testid="home-popup-preview-dot"
                aria-current={i === index ? 'true' : undefined}
                className={
                  i === index
                    ? 'h-2 w-[22px] rounded-full bg-brand-yellow'
                    : 'h-2 w-2 rounded-full bg-[#7c7c7c]'
                }
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
